
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { useTranslation } from 'react-i18next';

const PackagesOverview = () => {
  const { t } = useTranslation();
  
  // Define the packages data
  const packages = [
    {
      id: "snabbstart",
      title: t('packages.quickStart.title'),
      description: t('packages.quickStart.description'),
      shortDesc: t('packages.quickStart.shortDesc'),
      features: t('packages.quickStart.features', { returnObjects: true }) as Array<{name: string, reason: string}>,
      timeEstimate: t('packages.quickStart.timeEstimate')
    },
    {
      id: "vardagspaketet",
      title: t('packages.everyday.title'),
      description: t('packages.everyday.description'),
      shortDesc: t('packages.everyday.shortDesc'),
      features: t('packages.everyday.features', { returnObjects: true }) as Array<{name: string, reason: string}>,
      timeEstimate: t('packages.everyday.timeEstimate')
    },
    {
      id: "full-frikoppling",
      title: t('packages.fullSwitch.title'),
      description: t('packages.fullSwitch.description'),
      shortDesc: t('packages.fullSwitch.shortDesc'),
      features: t('packages.fullSwitch.features', { returnObjects: true }) as Array<{name: string, reason: string}>,
      timeEstimate: t('packages.fullSwitch.timeEstimate')
    },
    {
      id: "sociala-media",
      title: t('packages.socialMedia.title'),
      description: t('packages.socialMedia.description'),
      shortDesc: t('packages.socialMedia.shortDesc'),
      features: t('packages.socialMedia.features', { returnObjects: true }) as Array<{name: string, reason: string}>,
      timeEstimate: t('packages.socialMedia.timeEstimate')
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t('packages.seo.title')}
        description={t('packages.seo.description')}
        url="/paket"
      />
      <Navbar />
      <main className="flex-grow">
        <div className="bg-frikopplad-cream py-16">
          <div className="container-custom">
            <h1 className="text-4xl font-bold text-frikopplad-navy mb-2">{t('packages.header.title')}</h1>
            <p className="text-xl text-gray-600 mb-4">{t('packages.header.subtitle')}</p>
            <p className="max-w-3xl text-gray-700">
              {t('packages.header.description')}
            </p>
          </div>
        </div>
        
        <div className="container-custom py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {packages.map((pkg) => (
              <div key={pkg.id} className={`border rounded-lg shadow-sm overflow-hidden bg-white relative ${pkg.id === "sociala-media" || pkg.id === "snabbstart" || pkg.id === "vardagspaketet" || pkg.id === "full-frikoppling" ? "isolation-auto" : ""}`}>
                {pkg.id === "sociala-media" && (
                  <div 
                    className="absolute inset-0 opacity-10 bg-contain bg-no-repeat bg-center pointer-events-none"
                    style={{
                      backgroundImage: "url('https://upload.wikimedia.org/wikipedia/commons/d/dd/Mastodon_mascot_vector_version.svg')"
                    }}
                  ></div>
                )}
                {pkg.id === "snabbstart" && (
                  <div 
                    className="absolute inset-0 opacity-10 bg-contain bg-no-repeat bg-center pointer-events-none"
                    style={{
                      backgroundImage: "url('https://upload.wikimedia.org/wikipedia/commons/7/7a/Firefox_brand_logo%2C_2019.svg')"
                    }}
                  ></div>
                )}
                {pkg.id === "vardagspaketet" && (
                  <div 
                    className="absolute inset-0 opacity-10 bg-contain bg-no-repeat bg-center pointer-events-none"
                    style={{
                      backgroundImage: "url('https://upload.wikimedia.org/wikipedia/commons/6/60/Nextcloud_Logo.svg')"
                    }}
                  ></div>
                )}
                {pkg.id === "full-frikoppling" && (
                  <div 
                    className="absolute inset-0 opacity-10 bg-contain bg-no-repeat bg-center pointer-events-none"
                    style={{
                      backgroundImage: "url('https://upload.wikimedia.org/wikipedia/commons/d/d8/Tux-flat.svg')"
                    }}
                  ></div>
                )}
                <div className="p-6 border-b relative">
                  <h2 className="text-2xl font-semibold text-frikopplad-navy">{pkg.title}</h2>
                  <p className="text-gray-600 mt-1">{pkg.description}</p>
                  <p className="text-sm text-gray-700 mt-4">{pkg.shortDesc}</p>
                </div>
                
                <div className="p-6 relative">
                  <h3 className="text-sm font-semibold text-gray-500 mb-4">{t('packages.featuresLabel')}</h3>
                  <ul className="space-y-3">
                    {Array.isArray(pkg.features) && pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="text-frikopplad-blue h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">{feature.name}</p>
                          <p className="text-sm text-gray-500">{feature.reason}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500">
                        {t('packages.timeLabel')}: {pkg.timeEstimate}
                      </p>
                      <Button className="bg-frikopplad-blue hover:bg-frikopplad-navy" asChild>
                        <Link to={`/paket/${pkg.id}`}>
                          {t('packages.guideButton')} <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PackagesOverview;
